import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { LocalizationService } from '../../../localization/localization.service';
import { IFormatAsNumberOptions } from '../../../shared/directives/format-as-number/format-as-number.directive';
import { PercentagePipe } from '../../../shared/pipes/percentage/percentage.pipe';
import { FormAlignmentsEnum } from '../../form-master.enum';
import { IFormFieldErrorMessages } from '../../form-master.interface';
import { CustomFormFieldComponent } from '../custom-form-field/custom-form-field.component';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-percentage-field',
  templateUrl: './percentage-field.component.html',
  styleUrls: ['./percentage-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PercentageFieldComponent),
      multi: true,
    },
  ],
})
export class PercentageFieldComponent
  extends CustomFormFieldComponent
  implements ControlValueAccessor, OnInit {
  @Input() alignment: FormAlignmentsEnum;
  @Input() label: string;
  @Input() hideLabel: boolean;
  @Input() fieldId: string;
  @Input() condensed: boolean;
  @Input() locale: string;
  @Input() decimalDigits = 2;
  @Input() max = Infinity;
  @Input() min = -Infinity;
  @Input() formControl: FormControl;
  @Input() errorMessages: IFormFieldErrorMessages;
  @Input() e2eId: string;

  @Output() onFocus: EventEmitter<void> = new EventEmitter<void>();
  @Output() onBlur: EventEmitter<void> = new EventEmitter<void>();

  internalFormControl: AbstractControl;
  initialValue: string;
  formatAsNumberOptions: IFormatAsNumberOptions;

  constructor(
    changeDetectorRef: ChangeDetectorRef,
    translateService: TranslateService,
    private _localizationService: LocalizationService,
    private _percentagePipe: PercentagePipe
  ) {
    super(changeDetectorRef, translateService);
  }

  ngOnInit() {
    this.formatAsNumberOptions = {
      onBlur: this._convertToPercentage.bind(this),
      decimalDigits: this.decimalDigits,
      decimalSeparator: this._localizationService.getDecimalSeparator(
        this.locale
      ),
      max: this.max,
      min: this.min,
    };

    super.ngOnInit();
  }

  writeValue(value: any) {
    if (!this.internalFormControl) {
      this.initFormControl(value);
    } else {
      this.internalFormControl.setValue(
        this._percentagePipe.transform(value, this.locale, this.decimalDigits),
        { emitEvent: false }
      );
    }
  }

  setDisabledState(isDisabled: boolean) {
    const onlySelf = { onlySelf: true, emitEvent: false };

    if (isDisabled) {
      this.internalFormControl.disable(onlySelf);
    } else {
      this.internalFormControl.enable(onlySelf);
    }
  }

  initFormControl(value: any) {
    this.internalFormControl = new FormControl(value || 0);
    this.initialValue = this._convertToPercentage(value || 0);

    this.internalFormControl.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe((currentAmount) => {
        const formattedAmount = currentAmount
          ? currentAmount.replace(
              this.formatAsNumberOptions.decimalSeparator,
              '.'
            )
          : 0;

        this.propagateChange(parseFloat(formattedAmount));
      });

    this.formControl.statusChanges
      .pipe(takeUntil(this.destroyed), distinctUntilChanged())
      .subscribe(() => this.changeDetectorRef.markForCheck());
  }

  private _convertToPercentage(value: any): string {
    return this._percentagePipe.transform(
      value,
      this.locale,
      this.decimalDigits
    ) as string;
  }

  get errorMessage(): string {
    const errors = this.formControl.errors;

    return this.getTranslatedErrorMessage(errors);
  }
}
